/**
 * custom-shipping-estimate.js
 * CCC custom feature — live freight rates from a postcode, on the product
 * page (dialog) and the cart page (inline card).
 *
 * ONE element, TWO strategies, because Shopify has no single API for both:
 *
 * CART MODE (no configuration): Ajax Cart API — same-origin, no token, same
 * rate engine as checkout, carrier rates included:
 *   POST /cart/prepare_shipping_rates.json  → starts the calculation
 *   GET  /cart/async_shipping_rates.json    → null while pending, then rates
 * The GET matches the POST by its exact query params.
 *
 * PRODUCT MODE (Storefront public token, Headless channel): those Ajax
 * endpoints price the CART only, so a single variant is priced by creating a
 * throwaway Storefront cart (oneTimeUse address, never the real cart) and
 * reading deliveryGroups(withCarrierRates: true) — which REQUIRES @defer,
 * hence the multipart/mixed parser below.
 *
 * Honesty rules proven the hard way this feature exists to encode:
 * - Carrier delivery_range assumes same-day lodgement (verified live: an
 *   Express quote after the 1pm cutoff still promised next-day). ETAs are
 *   re-anchored on the REAL despatch window: max(1pm-cutoff processing date,
 *   roast/preorder ready window from the dispatch manager) + transit.
 * - Local Delivery postcodes get only the native local option from Storefront
 *   carts (probed exhaustively — no API lever unmasks couriers). Never render
 *   that as "we don't ship there".
 * - Empty rate lists are never cached: one transient carrier hiccup must not
 *   repeat all session.
 * - The unified shipping discount is an order-total automatic discount at
 *   checkout, not freight credit per method. Rows show true carrier prices;
 *   a footnote explains the flat −$X off the order total so cheaper methods
 *   stay comparable.
 * - Direct-carrier APIs (Starshipit) are out: secret keys can't ship in
 *   browser code, and they can't see Shopify-native rates (Local Delivery).
 */

import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import {
  getEffectiveProcessingDate,
  getLastReadyWindow,
  addBusinessDays,
  getDispatchHolidayAdvisory,
} from '@theme/custom-dispatch-manager';
import { getSydneyZoneLabel } from '@theme/custom-shared-public-holidays';
import { lockScroll, unlockScroll, onAnimationEnd, debounce, requestIdleCallback } from '@theme/utilities';

/** Storefront API version. Bump deliberately — @defer needs >= 2024-07. */
const API_VERSION = '2026-07';

/** Carrier calls are slow; poll rather than hammer. */
const POLL_INTERVAL_MS = 750;
const POLL_TIMEOUT_MS = 20000;

/**
 * Re-quote coalescing window after a confirmed cart change — long enough to
 * absorb the next click of a +/- spinner, not the round-trip itself.
 */
const RECHECK_DEBOUNCE_MS = 400;

/**
 * "Used the estimator before" decays: without a TTL, one use years ago means
 * a background carrier round-trip on every product view forever.
 */
const ENGAGED_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Australian postcode → state. Both spellings are needed: the Ajax API
 * matches provinces by NAME, CartDeliveryAddressInput wants the CODE.
 * Ranges per Australia Post allocations — the gaps matter (2600-2618 and
 * 2900-2920 are ACT carved out of the NSW 2000-2999 block).
 */
const AU_STATE_RANGES = [
  ['NSW', 'New South Wales', 1000, 1999],
  ['NSW', 'New South Wales', 2000, 2599],
  ['NSW', 'New South Wales', 2619, 2899],
  ['NSW', 'New South Wales', 2921, 2999],
  ['ACT', 'Australian Capital Territory', 200, 299],
  ['ACT', 'Australian Capital Territory', 2600, 2618],
  ['ACT', 'Australian Capital Territory', 2900, 2920],
  ['VIC', 'Victoria', 3000, 3999],
  ['VIC', 'Victoria', 8000, 8999],
  ['QLD', 'Queensland', 4000, 4999],
  ['QLD', 'Queensland', 9000, 9999],
  ['SA', 'South Australia', 5000, 5999],
  ['WA', 'Western Australia', 6000, 6999],
  ['TAS', 'Tasmania', 7000, 7999],
  ['NT', 'Northern Territory', 800, 999],
];

const CART_CREATE_MUTATION = `
  mutation CccEstimateCart($input: CartInput!) {
    cartCreate(input: $input) {
      cart { id }
      userErrors { field message }
    }
  }
`;

/** withCarrierRates makes @defer mandatory — the response streams. */
const RATES_QUERY = `
  query CccEstimateRates($id: ID!) {
    cart(id: $id) {
      id
      ...CccRates @defer(label: "rates")
    }
  }
  fragment CccRates on Cart {
    deliveryGroups(first: 5, withCarrierRates: true) {
      nodes {
        deliveryOptions {
          title
          handle
          description
          estimatedCost { amount currencyCode }
        }
      }
    }
  }
`;

/**
 * Merchant transit times (BUSINESS days, by destination state) for carriers
 * whose live rates carry no delivery estimate. Source: Rhys, 21 Jul 2026.
 * NT is deliberately absent (no figure supplied) — those quotes show price only.
 */
const ROAD_FREIGHT_TRANSIT = {
  NSW: [1, 3], VIC: [1, 3], ACT: [1, 3],
  QLD: [2, 4], SA: [2, 4],
  WA: [5, 7], TAS: [5, 7],
};

/** TNT AIR: the road table minus one day (per Rhys), floored at 1. */
const AIR_FREIGHT_TRANSIT = Object.fromEntries(
  Object.entries(ROAD_FREIGHT_TRANSIT).map(([state, [min, max]]) => [state, [Math.max(1, min - 1), Math.max(1, max - 1)]])
);

/**
 * Flat business-day span for every AU state (incl. NT). Used for carriers
 * whose Storefront quotes have no delivery_range (AusPost, DHL on PDP).
 * @param {number} min
 * @param {number} max
 * @returns {Record<string, [number, number]>}
 */
function flatTransit(min, max) {
  const span = /** @type {[number, number]} */ ([min, max]);
  return {
    NSW: span,
    VIC: span,
    ACT: span,
    QLD: span,
    SA: span,
    WA: span,
    TAS: span,
    NT: span,
  };
}

/** Rhys 21 Jul 2026 — PDP Storefront fallbacks (cart Ajax already has Starshipit dates). */
const AUSPOST_EXPRESS_TRANSIT = flatTransit(1, 2);
const AUSPOST_STANDARD_TRANSIT = flatTransit(2, 7);
const DHL_TRANSIT = flatTransit(1, 2);

/**
 * Rate-name fragment (lowercased match) → transit table. Consulted only when
 * the carrier itself supplied no estimate — live data always wins.
 * Express before standard so "eParcel Express" does not hit the standard row.
 */
const FALLBACK_TRANSIT = [
  ['eparcel express', AUSPOST_EXPRESS_TRANSIT],
  ['eparcel standard', AUSPOST_STANDARD_TRANSIT],
  ['dhl', DHL_TRANSIT],
  ['tnt air', AIR_FREIGHT_TRANSIT],
  ['tnt road', ROAD_FREIGHT_TRANSIT],
  ['direct freight', ROAD_FREIGHT_TRANSIT],
];

/** Always shown under the rates whenever any ETA is displayed. */
const REGIONAL_NOTE = 'Delivery dates are estimates — regional areas may take longer.';

/**
 * Phaser does not offer Shopify Local Delivery. Keep CCC zone helpers below
 * for sync parity, but never inject that copy or the "Local Delivery zone"
 * footnote on this storefront.
 */
const SHOW_LOCAL_DELIVERY_POLICY = false;

/**
 * Theme-only Local Delivery explainer (checkout description stays short for
 * Shopify's 255-char limit). Mirrors CCC admin Local Delivery pricing + zones.
 * Gated by SHOW_LOCAL_DELIVERY_POLICY — inactive on Phaser.
 */
const LOCAL_DELIVERY_PRICING = 'Free from $100 · otherwise $3';
const LOCAL_DELIVERY_WINDOWS = 'Weekday delivery by our staff, 6–8am or 6–9pm.';
const LOCAL_DELIVERY_AREAS = [
  ['Gosford, Wyoming, Narara', 'most days'],
  ['Kariong to Woy Woy', 'most days'],
  ['Point Frederick, Erina, Springfield, Greenpoint', 'Wednesdays'],
  ['Wamberal to The Entrance and Tumbi', 'Saturdays'],
];

/**
 * Structured policy block under a Local Delivery rate row (PDP + cart).
 * @returns {HTMLElement}
 */
function buildLocalDeliveryDetail() {
  const wrap = document.createElement('div');
  wrap.className = 'cse-rate__local';

  const pricing = document.createElement('p');
  pricing.className = 'cse-rate__local-pricing';
  pricing.textContent = LOCAL_DELIVERY_PRICING;

  const windows = document.createElement('p');
  windows.className = 'cse-rate__local-windows';
  windows.textContent = LOCAL_DELIVERY_WINDOWS;

  const areas = document.createElement('ul');
  areas.className = 'cse-rate__local-areas';
  for (const [place, when] of LOCAL_DELIVERY_AREAS) {
    const li = document.createElement('li');
    const placeEl = document.createElement('span');
    placeEl.className = 'cse-rate__local-place';
    placeEl.textContent = place;
    const whenEl = document.createElement('span');
    whenEl.className = 'cse-rate__local-when';
    whenEl.textContent = when;
    li.append(placeEl, whenEl);
    areas.appendChild(li);
  }

  wrap.append(pricing, windows, areas);
  return wrap;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Small lightning glyph for the "Fast" tag. Built as SVG (not an emoji — the
 * store's copy is deliberately emoji-free) so it inherits currentColor.
 * @returns {SVGElement}
 */
function buildBoltIcon() {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'currentColor');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', 'M13 2 4 14h6l-1 8 9-12h-6z');
  svg.appendChild(path);

  return svg;
}

/**
 * CCC's unified shipping discount (policy page, commit cabc275): an AUTOMATIC
 * order discount at checkout that offsets freight, tiered by order value.
 * [threshold cents (exclusive), discount cents] — descending, first match
 * wins; the "over $X" label is derived from the threshold so the two can
 * never drift apart. NOTE: mirrors a discount configured in Shopify admin —
 * if the tiers change there, change them here.
 */
const SHIPPING_DISCOUNT_TIERS = [
  [200000, 5000],
  [100000, 2000],
  [40000, 1500],
  [25000, 1200],
  [15000, 800],
  [9900, 500],
];

/** Hoisted: Intl.NumberFormat construction is not free (~0.1-1ms each). */
const AUD_FORMAT = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });
const AUD_WHOLE_FORMAT = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });

/** @param {number} cents @returns {string} "$17.42" */
function formatMoney(cents) {
  return AUD_FORMAT.format(cents / 100);
}

/**
 * @param {number} basisCents order value the tier keys off — cart total in
 *   cart mode, item price × quantity in product mode (a floor: real baskets
 *   only ever earn a BIGGER discount, so the shown price is a safe ceiling)
 * @returns {{cents: number, label: string}|null}
 */
function shippingDiscountFor(basisCents) {
  for (const [threshold, cents] of SHIPPING_DISCOUNT_TIERS) {
    if (basisCents > threshold) return { cents, label: `over ${AUD_WHOLE_FORMAT.format(threshold / 100)}` };
  }

  return null;
}

/**
 * "Best value" weighs each transit day against money: an option's score is its
 * real freight plus a flat per-day time cost, so a big price jump for a small
 * speed gain (DHL over Direct Freight) sorts DOWN while cheap-and-fast sorts
 * UP — i.e. "the fastest option without paying a premium". Tunable: higher
 * favours speed, lower favours price.
 */
const VALUE_DAY_COST_CENTS = 800;

/**
 * A rate is flagged "Fast" when its whole transit window lands within this many
 * days (so "1–2 days" and "2 days" qualify, "1–3 days" does not). Highlights
 * the quickest tier across every sort, not just when sorted by speed.
 */
const FAST_TRANSIT_MAX_DAYS = 2;

/**
 * Transit midpoint in days for speed/value ranking. Rates with no estimate
 * (NT, unknown carrier) sort last rather than pretending to be instant.
 * @param {{transit: {min: number, max: number}|null}} model
 * @returns {number}
 */
function transitMid(model) {
  return model.transit ? (model.transit.min + model.transit.max) / 2 : Infinity;
}

/**
 * Sort modes offered above the rate list. Each is a comparator over rate
 * models (real gross price + transit span). "Cheapest" mirrors the order the
 * shopper meets at checkout; "Best value" uses the time-cost score above.
 */
const RATE_SORTS = {
  cheapest: {
    label: 'Cheapest',
    compare: (a, b) => a.grossCents - b.grossCents || transitMid(a) - transitMid(b),
  },
  fastest: {
    label: 'Fastest',
    compare: (a, b) =>
      (a.transit?.min ?? Infinity) - (b.transit?.min ?? Infinity) ||
      (a.transit?.max ?? Infinity) - (b.transit?.max ?? Infinity) ||
      a.grossCents - b.grossCents,
  },
  value: {
    label: 'Fastest value',
    compare: (a, b) =>
      a.grossCents +
      VALUE_DAY_COST_CENTS * transitMid(a) -
      (b.grossCents + VALUE_DAY_COST_CENTS * transitMid(b)) ||
      a.grossCents - b.grossCents,
  },
};

/** Display order of the sort control. */
const RATE_SORT_ORDER = ['cheapest', 'fastest', 'value'];

/**
 * @param {string} postcode 4-digit AU postcode
 * @returns {{code: string, name: string}|null} null if outside every allocated range
 */
function stateForPostcode(postcode) {
  const n = Number(postcode);
  if (!Number.isInteger(n)) return null;

  for (const [code, name, min, max] of AU_STATE_RANGES) {
    if (n >= min && n <= max) return { code, name };
  }

  return null;
}

/** @param {number} ms */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** @param {Date} date @param {number} n */
function addCalendarDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

/** Carriers don't deliver Sundays; Saturday is legitimate (AusPost does). */
function skipSunday(date) {
  return date.getDay() === 0 ? addCalendarDays(date, 1) : date;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Fri 24 Jul" — hand-rolled so browser and Node render identically. */
function formatDay(date) {
  return `${DAY_NAMES[date.getDay()]} ${date.getDate()} ${MONTH_NAMES[date.getMonth()]}`;
}

/**
 * "Thu 23 – Mon 27 Jul" — the month appears once when shared, both when the
 * range crosses months ("Thu 30 Jul – Mon 3 Aug"). Single day: "Thu 23 Jul".
 */
function formatDayRange(start, end) {
  if (start.getTime() === end.getTime()) return formatDay(start);

  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${DAY_NAMES[start.getDay()]} ${start.getDate()} – ${formatDay(end)}`;
  }

  return `${formatDay(start)} – ${formatDay(end)}`;
}

/**
 * Whole days from baseDate to an ISO date string, by calendar date. Parsed
 * from string parts — a bare 'YYYY-MM-DD' through new Date() lands at UTC
 * midnight, i.e. *yesterday* on every Australian clock.
 * @returns {number|null}
 */
function daysBetween(baseDate, iso) {
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;

  const target = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const base = Date.UTC(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
  return Math.round((target - base) / 86400000);
}

/**
 * Parse a GraphQL incremental-delivery (multipart/mixed) response body.
 * Boundary is literally "graphql"; each part is headers + blank line + JSON.
 * First payload carries `data`, deferred ones carry `incremental`. Only
 * consumes parts whose END boundary has arrived — never half-parses a chunk.
 * Falls back to plain JSON for application/json responses (cartCreate).
 *
 * @param {Response} response
 * @returns {Promise<object[]>} every payload received, in arrival order
 */
async function readIncrementalPayloads(response) {
  const contentType = response.headers.get('content-type') || '';

  if (!contentType.includes('multipart/mixed')) {
    return [await response.json()];
  }

  const reader = response.body?.getReader();
  if (!reader) return [];

  const decoder = new TextDecoder();
  const payloads = [];
  let buffer = '';

  const drain = () => {
    for (;;) {
      const start = buffer.indexOf('--graphql');
      if (start === -1) return;

      const next = buffer.indexOf('--graphql', start + 9);
      if (next === -1) return;

      const part = buffer.slice(start + 9, next);
      buffer = buffer.slice(next);

      // Terminating boundary is "--graphql--"; nothing to parse after it.
      if (part.startsWith('--')) return;

      const blankLine = part.search(/\r?\n\r?\n/);
      const body = (blankLine === -1 ? part : part.slice(blankLine)).trim();
      if (!body) continue;

      try {
        payloads.push(JSON.parse(body));
      } catch (error) {
        console.warn('[CCC] shipping-estimate: unparsable multipart chunk', error);
      }
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    drain();
  }

  buffer += decoder.decode();
  drain();

  return payloads;
}

class CccShippingEstimate extends HTMLElement {
  connectedCallback() {
    this._form = this.querySelector('[data-cse-form]');
    this._input = this.querySelector('[data-cse-postcode]');
    this._button = this.querySelector('[data-cse-submit]');
    this._output = this.querySelector('[data-cse-output]');
    // Dedicated terse live region — the visible results are NOT aria-live, so
    // re-sorting doesn't re-announce the whole panel (see _announce).
    this._live = this.querySelector('[data-cse-live]');

    if (!this._form || !this._input || !this._output) return;

    this._cartUrl = window.Theme?.routes?.cart_url || '/cart';
    this._token = this.dataset.token || '';
    this._mode = this.dataset.mode === 'product' ? 'product' : 'cart';
    this._bulkyGrams = Number(this.dataset.bulkyGrams) || 0;
    // Active rate-list sort; persists across re-quotes so a shopper who picked
    // "Fastest" keeps it when the quote refreshes.
    this._sortMode = 'cheapest';

    // Section scope, resolved once — the load-bearing quick-add/section
    // boundary convention (mirrors custom-variant-info.js).
    this._scope = this.closest('.shopify-section, dialog') || document;

    // Monotonic id: a quote landing while an earlier one is in flight
    // invalidates it, so a superseded run can't paint over the winner.
    this._requestId = 0;

    this._onSubmit = this._handleSubmit.bind(this);
    this._form.addEventListener('submit', this._onSubmit);

    if (this._mode === 'product') {
      this._setupProductMode();

      // Postcode resolution, most-recent-intent first: a postcode the shopper
      // TYPED (localStorage) beats the logged-in customer's default-address
      // postcode (Liquid-provided, AU-gated server-side), beats empty.
      const customerPostcode = (this.dataset.customerPostcode || '').trim();
      const knownPostcode =
        this._remembered() ||
        (/^\d{4}$/.test(customerPostcode) && stateForPostcode(customerPostcode) ? customerPostcode : '');
      if (knownPostcode) this._input.value = knownPostcode;

      // Stamp label BEFORE the chip becomes visible in the price slot so
      // returning shoppers never flash "Check delivery" → "Delivery to …".
      this._setTriggerSummary(knownPostcode);
      this._mountTrigger();

      // Auto-quote when we know where they are: they've used the estimator
      // recently, or they're logged in with an AU address on file. Scheduled
      // after window load + idle so it NEVER shares bandwidth with page assets.
      const shouldAutoQuote = !!knownPostcode && (this._engaged() || !!customerPostcode);

      if (shouldAutoQuote && !this.closest('dialog')) {
        this._whenSettled(() => this._autoQuoteProduct());
      }
    } else {
      this._setupCartMode();

      const customerPostcode = (this.dataset.customerPostcode || '').trim();
      const knownPostcode =
        this._remembered() ||
        (/^\d{4}$/.test(customerPostcode) && stateForPostcode(customerPostcode) ? customerPostcode : '');
      if (knownPostcode) this._input.value = knownPostcode;

      const shouldAutoQuote = !!knownPostcode && (this._engaged() || !!customerPostcode);

      if (shouldAutoQuote) {
        // Cart edits re-render the section (element rebuilt), so restoring on
        // connect doubles as the follow-up: the number follows the basket.
        this._whenSettled(() => this._autoQuote());
      }
    }
  }

  _setupProductMode() {
    try {
      this._variants = JSON.parse(this.querySelector('[data-cse-variants-json]')?.textContent || '[]');
    } catch (error) {
      console.warn('[CCC] shipping-estimate: failed to parse variant data', error);
      this._variants = [];
    }

    this._variantId = this.dataset.selectedVariantId || this._variants[0]?.id || null;
    // Scopes the rate cache to this product — a coffee bag and an espresso
    // machine at the same postcode+qty ship very differently and must not share
    // a cached quote (see _shippingFingerprint).
    this._productId = this.dataset.productId || '';
    this._lastFingerprint = null;

    this._onVariantChange = this._handleVariantChange.bind(this);
    document.addEventListener(ThemeEvents.variantSelected, this._onVariantChange);
    document.addEventListener(ThemeEvents.variantUpdate, this._onVariantChange);

    // A quote for 1 bag is wrong for 6.
    this._onQuantityChange = this._handleQuantityChange.bind(this);
    this._scope.addEventListener('change', this._onQuantityChange);

    this._dialog = this.querySelector('[data-cse-dialog]');
    this._trigger = this.querySelector('[data-cse-open]');
    this._triggerLabel = this.querySelector('[data-cse-trigger-label]');
    this._triggerNote = this.querySelector('[data-cse-trigger-note]');
    this._despatchEl = this.querySelector('[data-cse-despatch]');
    this._quoteQtyEl = this.querySelector('[data-cse-quote-qty]');
    this._rateGroupName = `cse-rate-${this._mode}-${Math.random().toString(36).slice(2, 9)}`;

    if (this._dialog && this._trigger) {
      this._onOpen = () => this._openDialog();
      this._onDialogClick = this._handleDialogClick.bind(this);
      // 'close' also fires on Esc — scroll must unlock on every exit path.
      this._onDialogClose = () => unlockScroll(this._dialog);
      // Esc parity with X/backdrop: run the same slide-out instead of the
      // native instant close.
      this._onDialogCancel = (event) => {
        event.preventDefault();
        this._closeDialog();
      };

      this._trigger.addEventListener('click', this._onOpen);
      this._dialog.addEventListener('click', this._onDialogClick);
      this._dialog.addEventListener('close', this._onDialogClose);
      this._dialog.addEventListener('cancel', this._onDialogCancel);

      // Mount is deferred to connectedCallback so the known-postcode label
      // can be stamped before the chip appears in the price slot.
    }
  }

  _setupCartMode() {
    // The cart card has no dialog-open moment to fill the despatch line on, so
    // paint it on connect; the rate render refreshes it alongside the ETAs.
    this._despatchEl = this.querySelector('[data-cse-despatch]');
    this._fillDespatch();
    this._onCartUpdate = this._handleCartUpdate.bind(this);
    this._debouncedRequote = debounce(() => this._autoQuote(), RECHECK_DEBOUNCE_MS);
    this._rateGroupName = `cse-rate-${this._mode}-${Math.random().toString(36).slice(2, 9)}`;

    // The real signal: Horizon's cart-items component dispatches the Shopify
    // STANDARD cartLinesUpdate event. The theme's own 'cart:update' constant
    // exists in events.js but nothing in stock Horizon dispatches it (only
    // the CCC wishlist page does) — listen to both.
    document.addEventListener(StandardEvents.cartLinesUpdate, this._onCartUpdate);
    document.addEventListener(ThemeEvents.cartUpdate, this._onCartUpdate);
    // Horizon 4.2.0: cart section re-rendered after a browser Back (no promise on it).
    document.addEventListener(ThemeEvents.cartSectionRestored, this._onCartUpdate);
  }

  disconnectedCallback() {
    this._form?.removeEventListener('submit', this._onSubmit);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this._onCartUpdate);
    document.removeEventListener(ThemeEvents.cartUpdate, this._onCartUpdate);
    document.removeEventListener(ThemeEvents.cartSectionRestored, this._onCartUpdate);
    document.removeEventListener(ThemeEvents.variantSelected, this._onVariantChange);
    document.removeEventListener(ThemeEvents.variantUpdate, this._onVariantChange);
    this._scope?.removeEventListener('change', this._onQuantityChange);
    this._trigger?.removeEventListener('click', this._onOpen);
    this._dialog?.removeEventListener('click', this._onDialogClick);
    this._dialog?.removeEventListener('close', this._onDialogClose);
    this._dialog?.removeEventListener('cancel', this._onDialogCancel);
    window.removeEventListener('load', this._onLoadIdle);
    this._debouncedRequote?.cancel();

    if (this._dialog?.open) {
      unlockScroll(this._dialog);
      this._dialog.close();
    }

    // Undo the adoption: no orphaned chip with a dead listener, restore the
    // dialog to its Liquid home, and give the block wrapper its display back.
    if (this._trigger && this._trigger.parentElement !== this) this._trigger.remove();
    this._restoreDialogHome();
    if (this._wrapper) this._wrapper.style.display = this._wrapperDisplay || '';

    this._abort?.abort();
  }

  /**
   * Run a callback after window load, then browser idle — background work
   * that must never compete with page rendering or asset bandwidth.
   */
  _whenSettled(callback) {
    const idle = () => requestIdleCallback(() => callback(), { timeout: 4000 });

    if (document.readyState === 'complete') {
      idle();
    } else {
      this._onLoadIdle = idle;
      window.addEventListener('load', this._onLoadIdle, { once: true });
    }
  }

  // ── Product-mode chrome ──────────────────────────────────────────────────

  _handleVariantChange(event) {
    if (!this._isSameProductContext(event)) return;

    // Variant switches morph the price block, which can replace the slot our
    // trigger lives in — the element survives in memory, so re-adopt once the
    // morph settles.
    setTimeout(() => this._mountTrigger(), 0);

    const resource = event.detail?.resource;
    if (!resource) return;

    // variant:selected may only carry an option-value id until the selection
    // resolves — that must not wipe a good quote. Prefer variantId; on
    // variant:update, resource.id is the variant id.
    const id =
      resource.variantId != null && resource.variantId !== ''
        ? resource.variantId
        : event.type === ThemeEvents.variantUpdate
          ? resource.id
          : null;

    if (!id || String(id) === String(this._variantId)) return;

    const qty = this._quantity();
    const prevFp = this._shippingFingerprint(this._variantId, qty);
    const nextId = String(id);
    const nextFp = this._shippingFingerprint(nextId, qty);

    this._variantId = nextId;

    // Colour / variant swaps at the same qty keep the pill and skip carrier
    // requote (fingerprint is qty-only). Discount tier can still move with
    // sale price — refresh net/struck/summary from cache when shown.
    if (prevFp === nextFp) {
      this._refreshRatesDiscount();
      return;
    }

    this._clearOutput();
    this._softRequoteProduct();
  }

  /**
   * Re-paint rates with live item×qty basis after a price-only variant swap
   * (same qty fingerprint). No Storefront round-trip.
   */
  _refreshRatesDiscount() {
    if (this._mode !== 'product') return;
    if (!this._output || this._output.hidden || !this._output.querySelector('.cse-rates')) return;

    const postcode = (this._input?.value || '').trim();
    if (!/^\d{4}$/.test(postcode) || !stateForPostcode(postcode)) return;

    const quantity = this._quantity();
    const signature = this._shippingFingerprint(this._variantId, quantity);
    const cached = this._cacheGet(postcode, signature);
    if (!cached?.rates?.length) return;

    this._renderRates(
      cached.rates,
      postcode,
      quantity,
      cached.quotedAt,
      this._productBasisCents(quantity)
    );
  }

  _handleQuantityChange(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (event.target.name !== 'quantity') return;

    const nextFp = this._shippingFingerprint(this._variantId, this._quantity());
    if (this._lastFingerprint != null && nextFp === this._lastFingerprint) return;

    this._clearOutput();
    this._softRequoteProduct();
  }

  /**
   * Quote signature: PRODUCT + quantity. Product-scoped so two different
   * products at the same postcode+qty never share a cached quote (a coffee bag
   * and an espresso machine ship very differently — the cache key is
   * `{postcode}:{signature}`, so a qty-only signature collided across products).
   * Variant/weight is still ignored: staff weight data is often blank or wrong
   * across colourways, so colour/grind swaps of the SAME product keep the quote
   * rather than force a clear/requote. Real mass changes at the same qty need
   * Check rates (or a qty change). Discount footnotes use live `_productBasisCents`.
   *
   * @param {string|number|null|undefined} _variantId unused (kept for call sites)
   * @param {number} quantity
   * @returns {string}
   */
  _shippingFingerprint(_variantId, quantity) {
    const qty = Number.isInteger(quantity) && quantity > 0 ? Math.min(quantity, 100) : 1;

    return `p${this._productId || 'x'}q${qty}`;
  }

  /**
   * After a fingerprint change, refill the pill when a postcode is already
   * known so it does not sit as a bare postcode with no price.
   */
  _softRequoteProduct() {
    if (this._mode !== 'product') return;

    const postcode = (this._input?.value || '').trim();
    if (!/^\d{4}$/.test(postcode) || !stateForPostcode(postcode)) return;

    this._autoQuoteProduct();
  }

  /**
   * Relocate the trigger chip into the price line's declared mount point
   * ([data-cse-slot] in blocks/price.liquid) so "Incl. GST · [chip]" reads as
   * one price cluster. Idempotent; re-run after variant morphs. Templates
   * without the slot (tax info hidden) keep the chip in the block, restyled
   * compact. The chip is display:none until one of these classes lands, so
   * pre-JS paints reserve no space and nothing shifts.
   */
  _mountTrigger() {
    if (!this._trigger) return;
    // Variant events fire in pairs — nothing to do unless a morph actually
    // detached the chip.
    if (this._mounted && this._trigger.isConnected) return;

    const slot = this._scope.querySelector('[data-cse-slot]');

    if (slot) {
      this._trigger.classList.add('cse-trigger--inline');
      this.classList.add('cse--adopted');

      // Dialog must leave the shopify-block BEFORE display:contents — an
      // ancestor with display:contents breaks <dialog> closed/top-layer
      // behaviour and can leak Delivery options into the PDP on load.
      this._hostDialogOnBody();

      // Collapse the (now empty-looking) Horizon block wrapper so it leaves
      // no phantom gap row. Inline style, stamped once — cheaper than a
      // global :has() rule evaluated on every page.
      const wrapper = this.closest('.shopify-block');
      if (wrapper instanceof HTMLElement && !this._wrapper) {
        this._wrapper = wrapper;
        this._wrapperDisplay = wrapper.style.display;
        wrapper.style.display = 'contents';
      }

      if (this._trigger.parentElement !== slot) slot.appendChild(this._trigger);

      // The estimator supersedes the boilerplate line. The block's scoped
      // <style> handles the section instance; this covers contexts that lose
      // the section wrapper (quick-add fragments).
      this._scope.querySelector('.tax-note__shipping')?.setAttribute('hidden', '');
    } else {
      this._trigger.classList.add('cse-trigger--compact');
    }

    this._mounted = true;
  }

  /**
   * Reparent the estimate <dialog> onto document.body so chip adoption can
   * safely use display:contents on .shopify-block without breaking dialog
   * closed-state or showModal() top layer.
   */
  _hostDialogOnBody() {
    if (!this._dialog || this._dialogHosted) return;
    if (this._dialog.parentElement === document.body) {
      this._dialogHosted = true;
      return;
    }

    this._dialogHome = {
      parent: this._dialog.parentNode,
      next: this._dialog.nextSibling,
    };
    document.body.appendChild(this._dialog);
    this._dialogHosted = true;
  }

  /** Put the dialog back under the custom element (disconnect / teardown). */
  _restoreDialogHome() {
    if (!this._dialog || !this._dialogHosted) return;

    const home = this._dialogHome;
    if (home?.parent?.isConnected) {
      home.parent.insertBefore(this._dialog, home.next);
    } else if (this.isConnected) {
      this.appendChild(this._dialog);
    } else {
      this._dialog.remove();
    }

    this._dialogHosted = false;
    this._dialogHome = null;
  }

  /**
   * Quick-add dialogs fire variant events for OTHER products on this page;
   * without this guard a PDP estimate would re-target itself to whatever the
   * shopper hovered in a product grid. Mirrors custom-variant-info.js.
   */
  _isSameProductContext(event) {
    if (!(event.target instanceof HTMLElement)) return false;

    const sourceSection = event.target.closest('.shopify-section, dialog');
    const hostSection = this._scope === document ? null : this._scope;

    return !sourceSection || !hostSection || sourceSection === hostSection;
  }

  /** Quantity from the PDP's own selector, so 6 bags quote as 6 bags. */
  _quantity() {
    const field = this._scope.querySelector('input[name="quantity"], quantity-selector input');
    const qty = Number(field?.value);

    return Number.isInteger(qty) && qty > 0 ? Math.min(qty, 100) : 1;
  }

  /**
   * Open the estimate dialog. A remembered postcode auto-runs the quote
   * (cache makes repeats instant); first-timers land focused in the field.
   */
  _openDialog() {
    if (!this._dialog || this._dialog.open) return;

    lockScroll(this._dialog);
    this._dialog.showModal();
    this._fillDespatch();
    this._fillQuoteQty();

    const postcode = (this._input.value || '').trim();

    if (/^\d{4}$/.test(postcode)) {
      if (!this._output.textContent.trim()) this._form.requestSubmit();
    } else {
      this._input.focus();
    }
  }

  /** "Quoting for 2" under the dialog product title. */
  _fillQuoteQty() {
    if (!this._quoteQtyEl) {
      this._quoteQtyEl =
        this._dialog?.querySelector('[data-cse-quote-qty]') ||
        this.querySelector('[data-cse-quote-qty]');
    }
    if (!this._quoteQtyEl) return;

    const qty = this._quantity();
    this._quoteQtyEl.hidden = false;
    this._quoteQtyEl.textContent = `Quoting for ${qty}`;
  }

  /** Close with the theme's .dialog-closing slide-out, matching stock dialogs. */
  async _closeDialog() {
    const dialog = this._dialog;
    if (!dialog?.open) return;

    dialog.classList.add('dialog-closing');

    // Race a hard timeout: if the animation finished before the listener
    // attached (or reduced-motion collapsed it), waiting on animationend
    // alone hangs the close and leaves scroll locked forever.
    await Promise.race([onAnimationEnd(dialog, undefined, { subtree: false }), wait(400)]);

    dialog.classList.remove('dialog-closing');
    unlockScroll(dialog);
    dialog.close();
  }

  _handleDialogClick(event) {
    if (event.target instanceof Element && event.target.closest('[data-cse-close]')) {
      this._closeDialog();
      return;
    }

    // A click on the backdrop registers on the <dialog> element itself.
    if (event.target === this._dialog) this._closeDialog();
  }

  /**
   * "Despatches Wed 22 Jul · order cutoff 1pm AEST" in the dialog footer and
   * the cart card. When an NSW public holiday sits between today and the
   * despatch window it says so, so the later date never looks like a mistake.
   */
  _fillDespatch() {
    if (!this._despatchEl) return;

    const { min, max } = this._despatchWindow();
    let text = `Despatches ${formatDayRange(min, max)} · order cutoff 1pm ${getSydneyZoneLabel()}`;
    const holiday = getDispatchHolidayAdvisory(max);
    if (holiday) text += ` · ${holiday}`;
    this._despatchEl.textContent = text;
  }

  // ── Quote engine ─────────────────────────────────────────────────────────

  /**
   * The one quote runner: cache → busy/skeleton → fetch → cache → render,
   * all requestId-guarded. `silent` marks background runs — errors and empty
   * results paint nothing (nobody asked), while interactive runs get honest
   * messages.
   *
   * @param {{postcode: string, province: {code: string, name: string}, quantity: number, silent: boolean}} args
   */
  async _runQuote({ postcode, province, quantity, silent }) {
    const requestId = ++this._requestId;

    // Destination on the pill immediately. Pass one arg so an existing price
    // note is kept (stale-while-revalidate) — clearing it here caused CLS.
    if (this._mode === 'product') this._setTriggerSummary(postcode);

    try {
      let signature;
      let basisCents;

      if (this._mode === 'product') {
        signature = this._shippingFingerprint(this._variantId, quantity);
        basisCents = this._productBasisCents(quantity);
      } else {
        const cart = await this._cartSignature();
        signature = cart.signature;
        basisCents = cart.totalCents;
      }

      const cached = this._cacheGet(postcode, signature);

      if (cached) {
        if (requestId === this._requestId) this._renderRates(cached.rates, postcode, quantity, cached.quotedAt, basisCents);
        return;
      }

      this._setBusy(true);
      this._renderPending();

      const rates =
        this._mode === 'product'
          ? await this._quoteProduct(postcode, province.code, quantity)
          : await this._quote(postcode, province.name);

      this._cacheSet(postcode, signature, rates);
      if (requestId !== this._requestId) return;

      if (rates.length || !silent) this._renderRates(rates, postcode, quantity, new Date(), basisCents);
      else this._clearOutput();
    } catch (error) {
      // A superseded run aborts by design — not a failure worth showing.
      if (requestId !== this._requestId) return;

      console.warn('[CCC] shipping-estimate: quote failed', error);

      if (silent) {
        this._clearOutput();
      } else {
        this._renderMessage(
          "We couldn't reach the carrier just now. Checkout will still show live rates, or contact us for a quote.",
          'error'
        );
      }
    } finally {
      if (requestId === this._requestId) this._setBusy(false);
    }
  }

  async _handleSubmit(event) {
    event.preventDefault();

    const postcode = (this._input.value || '').trim();

    if (!/^\d{4}$/.test(postcode)) {
      this._renderMessage('Enter a 4-digit Australian postcode.', 'error');
      this._input.focus();
      return;
    }

    const province = stateForPostcode(postcode);
    if (!province) {
      this._renderMessage(`${postcode} isn't an Australian postcode we recognise — check it, or contact us.`, 'error');
      this._input.focus();
      return;
    }

    if (this._mode === 'product' && !this._token) {
      this._renderMessage('Shipping rates are not configured yet.', 'error');
      return;
    }

    if (this._mode === 'product' && !this._variantId) {
      this._renderMessage('Choose your options first, then check shipping.', 'error');
      return;
    }

    this._remember(postcode);
    this._markEngaged();

    await this._runQuote({
      postcode,
      province,
      quantity: this._mode === 'product' ? this._quantity() : 1,
      silent: false,
    });
  }

  /**
   * CART restore/refresh without the shopper pressing anything — on connect
   * (section rebuilds destroy instance state) and after confirmed cart edits.
   */
  async _autoQuote() {
    if (!this.isConnected) return;

    // data-skip-morph should preserve the input across cart morphs, but if
    // any render path still wipes it, fall back to the remembered postcode —
    // and put it back in the field so UI matches quote.
    let postcode = (this._input.value || '').trim();
    if (!/^\d{4}$/.test(postcode)) {
      postcode = this._remembered();
      if (/^\d{4}$/.test(postcode)) this._input.value = postcode;
    }

    const province = /^\d{4}$/.test(postcode) ? stateForPostcode(postcode) : null;
    if (!province) return;

    await this._runQuote({ postcode, province, quantity: 1, silent: true });
  }

  /**
   * PRODUCT background quote: pre-answer "how much is shipping?" for shoppers
   * whose postcode we already know. Success paints the rates into the
   * (closed) dialog and the summary onto the trigger chip.
   */
  async _autoQuoteProduct() {
    if (!this.isConnected) return;

    const postcode = (this._input.value || '').trim();
    const province = /^\d{4}$/.test(postcode) ? stateForPostcode(postcode) : null;
    if (!province || !this._token || !this._variantId) return;

    // Sold-out variants add to a Storefront cart at quantity 0 → empty
    // delivery group. Pointless round-trip; a manual submit still runs and
    // gets the honest empty-state message.
    const variant = this._variants?.find((v) => String(v.id) === String(this._variantId));
    if (variant && variant.available === false) return;

    await this._runQuote({ postcode, province, quantity: this._quantity(), silent: true });
  }

  /**
   * A cart edit means the displayed quote no longer describes the basket.
   * CartLinesUpdateEvent is dispatched BEFORE its fetch resolves, carrying a
   * promise that settles when the server has applied the change — requoting
   * earlier would read the OLD cart and cache a stale quote. The debounce
   * coalesces rapid +/+/+ clicks into one carrier call.
   */
  _handleCartUpdate(event) {
    if (!this._engaged()) return;

    const settled = event?.promise instanceof Promise ? event.promise : Promise.resolve();

    settled
      .then(() => this._debouncedRequote())
      .catch(() => {
        /* the cart update itself failed — nothing changed, nothing to requote */
      });
  }

  /**
   * Cache identity AND discount basis for the current basket: any add,
   * remove or quantity change moves item_count or total_price.
   *
   * @returns {Promise<{signature: string, totalCents: number}>}
   */
  async _cartSignature() {
    const cart = await (await fetch(`${this._cartUrl}.js`, { headers: { Accept: 'application/json' } })).json();
    return { signature: `${cart.item_count}:${cart.total_price}`, totalCents: cart.total_price || 0 };
  }

  /**
   * Product-mode discount basis: item price × quantity, a FLOOR of the real
   * basket — the discount shown never overstates.
   * @param {number} quantity
   * @returns {number} cents
   */
  _productBasisCents(quantity) {
    const variant = this._variants?.find((v) => String(v.id) === String(this._variantId));
    const priceCents = Number(variant?.price);
    return Number.isFinite(priceCents) ? priceCents * quantity : 0;
  }

  /**
   * Has the shopper used the estimator in the last 30 days? localStorage so
   * "type it once" survives visits; the TTL stops one ancient use costing a
   * carrier round-trip on every product view forever.
   */
  _engaged() {
    try {
      const stamp = Number(localStorage.getItem('ccc:estimate-engaged'));
      return Number.isFinite(stamp) && Date.now() - stamp < ENGAGED_TTL_MS;
    } catch {
      return false;
    }
  }

  _markEngaged() {
    try {
      localStorage.setItem('ccc:estimate-engaged', String(Date.now()));
    } catch {
      /* private browsing — auto-refresh is a nicety, not a requirement */
    }
  }

  /** @returns {string} postcode the shopper used last, or '' */
  _remembered() {
    try {
      return localStorage.getItem('ccc:estimate-postcode') || '';
    } catch {
      return '';
    }
  }

  /** @param {string} postcode */
  _remember(postcode) {
    try {
      localStorage.setItem('ccc:estimate-postcode', postcode);
    } catch {
      /* private browsing — remembering is a nicety, not a requirement */
    }
  }

  /**
   * Cached entries carry when they were quoted: carrier delivery_range is
   * relative to the quote moment, so a replay hours later must convert its
   * dates against the ORIGINAL quote date. (v2 prefix — v1 stored bare arrays.)
   *
   * @param {string} postcode
   * @param {string} signature
   * @returns {{quotedAt: Date, rates: object[]}|null}
   */
  _cacheGet(postcode, signature) {
    try {
      const raw = sessionStorage.getItem(`ccc:estimate:v2:${postcode}:${signature}`);
      if (!raw) return null;

      const entry = JSON.parse(raw);
      if (!entry || !Array.isArray(entry.rates)) return null;

      const quotedAt = new Date(entry.quotedAt);
      return { quotedAt: Number.isNaN(quotedAt.getTime()) ? new Date() : quotedAt, rates: entry.rates };
    } catch {
      return null;
    }
  }

  /**
   * @param {string} postcode
   * @param {string} signature
   * @param {object[]} rates
   */
  _cacheSet(postcode, signature, rates) {
    // Never cache an empty answer: "no rates" can be a transient carrier
    // hiccup, and a poisoned entry would repeat it all session (found live:
    // a sold-out variant cached [] and every retry "hit" it).
    if (!rates.length) return;

    try {
      sessionStorage.setItem(
        `ccc:estimate:v2:${postcode}:${signature}`,
        JSON.stringify({ quotedAt: new Date().toISOString(), rates })
      );
    } catch {
      /* quota or private browsing — caching is an optimisation, not a feature */
    }
  }

  // ── Rate fetching ────────────────────────────────────────────────────────

  /**
   * CART MODE. prepare + poll: carrier calls are slow and the GET matches the
   * pending calculation by its exact query params — byte-identical strings.
   *
   * @param {string} postcode
   * @param {string} province full state name
   * @returns {Promise<object[]>} raw Ajax API shipping_rates entries
   */
  async _quote(postcode, province) {
    this._abort?.abort();
    this._abort = new AbortController();
    const { signal } = this._abort;

    const query = new URLSearchParams({
      'shipping_address[zip]': postcode,
      'shipping_address[country]': 'Australia',
      'shipping_address[province]': province,
    }).toString();

    const prepared = await fetch(`${this._cartUrl}/prepare_shipping_rates.json?${query}`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      signal,
    });

    if (!prepared.ok) throw new Error(`prepare_shipping_rates ${prepared.status}`);

    const deadline = Date.now() + POLL_TIMEOUT_MS;

    for (;;) {
      await wait(POLL_INTERVAL_MS);

      const response = await fetch(`${this._cartUrl}/async_shipping_rates.json?${query}`, {
        headers: { Accept: 'application/json' },
        signal,
      });

      if (!response.ok) throw new Error(`async_shipping_rates ${response.status}`);

      // Still calculating → literal null. Ready → { shipping_rates: [...] }.
      const payload = await response.json();
      if (payload && Array.isArray(payload.shipping_rates)) return payload.shipping_rates;

      if (Date.now() > deadline) throw new Error('carrier rate calculation timed out');
    }
  }

  /**
   * PRODUCT MODE. Price one variant via a throwaway Storefront cart,
   * normalised to the Ajax rate shape so rendering is shared.
   *
   * @param {string} postcode
   * @param {string} provinceCode
   * @param {number} quantity
   * @returns {Promise<object[]>}
   */
  async _quoteProduct(postcode, provinceCode, quantity) {
    this._abort?.abort();
    this._abort = new AbortController();
    const timer = setTimeout(() => this._abort.abort(), POLL_TIMEOUT_MS);

    try {
      const created = await this._storefront(CART_CREATE_MUTATION, {
        input: {
          lines: [{ merchandiseId: `gid://shopify/ProductVariant/${this._variantId}`, quantity }],
          delivery: {
            addresses: [
              {
                address: { deliveryAddress: { countryCode: 'AU', zip: postcode, provinceCode } },
                selected: true,
                // Nothing persists against the buyer — this cart is scratch.
                oneTimeUse: true,
              },
            ],
          },
        },
      });

      const payload = created[0]?.data?.cartCreate;
      const userErrors = payload?.userErrors || [];
      if (userErrors.length) throw new Error(userErrors.map((e) => e.message).join('; '));

      const cartId = payload?.cart?.id;
      if (!cartId) throw new Error('cartCreate returned no cart');

      const payloads = await this._storefront(RATES_QUERY, { id: cartId });

      // Rates arrive in the DEFERRED payload — merge every chunk and look in
      // both places (a static-rate fallback answers in the first one).
      const groups = payloads.flatMap((chunk) => {
        const direct = chunk?.data?.cart?.deliveryGroups?.nodes || [];
        const deferred = (chunk?.incremental || []).flatMap((part) => part?.data?.deliveryGroups?.nodes || []);
        return [...direct, ...deferred];
      });

      const seen = new Set();
      const rates = [];

      for (const group of groups) {
        for (const option of group?.deliveryOptions || []) {
          const key = `${option.title}:${option.estimatedCost?.amount}`;
          if (seen.has(key)) continue;
          seen.add(key);

          rates.push({
            name: option.title || 'Shipping',
            presentment_name: option.title || 'Shipping',
            price: String(option.estimatedCost?.amount ?? '0'),
            currency: option.estimatedCost?.currencyCode || 'AUD',
            description: option.description || '',
            delivery_range: null,
            delivery_days: [],
          });
        }
      }

      return rates;
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * @param {string} query
   * @param {object} variables
   * @returns {Promise<object[]>} one entry per multipart payload
   */
  async _storefront(query, variables) {
    const response = await fetch(`/api/${API_VERSION}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'multipart/mixed, application/json',
        'X-Shopify-Storefront-Access-Token': this._token,
      },
      body: JSON.stringify({ query, variables }),
      signal: this._abort.signal,
    });

    if (!response.ok) throw new Error(`Storefront API ${response.status}`);

    const payloads = await readIncrementalPayloads(response);
    const errors = payloads.flatMap((p) => p?.errors || []);
    if (errors.length) throw new Error(errors.map((e) => e.message).join('; '));

    return payloads;
  }

  // ── ETA computation ──────────────────────────────────────────────────────

  /**
   * When can this order actually leave the building? The dispatch manager's
   * memoized ready window (roast/preorder holds, computed by roast-ui's cart
   * paint) when present, else the cutoff-aware next despatch date. Never
   * earlier than the effective processing date: roast "ready today" still
   * can't despatch today after 1pm.
   *
   * @returns {{min: Date, max: Date}}
   */
  _despatchWindow() {
    const effective = getEffectiveProcessingDate();

    if (this._mode === 'cart') {
      const ready = getLastReadyWindow();

      if (ready) {
        const lo = ready.min > effective ? ready.min : effective;
        const hi = ready.max > lo ? ready.max : lo;
        return { min: lo, max: hi };
      }
    }

    return { min: effective, max: effective };
  }

  /**
   * Transit time for a rate: the carrier's own estimate when supplied, else
   * the merchant table for carriers that never do (Direct Freight, TNT).
   * Carrier delivery_range is ABSOLUTE dates assuming same-day lodgement, so
   * it converts to transit days relative to the QUOTE date for re-anchoring.
   *
   * @param {object} rate
   * @param {{quoteDate: Date, stateCode: string|null}} ctx
   * @returns {{min: number, max: number, unit: 'calendar'|'business'}|null}
   */
  _transitForRate(rate, ctx) {
    const range = rate.delivery_range;

    if (Array.isArray(range) && range[0]) {
      const min = daysBetween(ctx.quoteDate, range[0]);
      const max = daysBetween(ctx.quoteDate, range[1] ?? range[0]);

      // Sanity gates: a past or absurd range is not data to build copy on.
      // Garbage from a carrier that DID answer must not fall through to the
      // merchant table — better silent than wrong.
      if (min !== null && min >= 0 && min <= 60) {
        const hi = max !== null && max >= min ? max : min;
        if (hi > 0) return { min: Math.max(1, min), max: hi, unit: 'calendar' };
      }

      return null;
    }

    if (Array.isArray(rate.delivery_days) && rate.delivery_days.length) {
      const days = rate.delivery_days.filter((d) => Number.isFinite(d) && d > 0);
      if (days.length) return { min: Math.min(...days), max: Math.max(...days), unit: 'business' };
    }

    const name = String(rate.presentment_name || rate.name || '').toLowerCase();
    for (const [fragment, table] of FALLBACK_TRANSIT) {
      if (name.includes(fragment)) {
        const span = ctx.stateCode ? table[ctx.stateCode] : null;
        // No entry (NT, unknown state) → no ETA rather than an invented one.
        return span ? { min: span[0], max: span[1], unit: 'business' } : null;
      }
    }

    return null;
  }

  /**
   * Delivery estimate for a rate as BOTH a transit span (for sorting) and the
   * shopper-facing "1–2 days · Mon 27 – Wed 29 Jul" line. The day count is the
   * carrier's own estimate (or the merchant table's); the DATE range re-anchors
   * it on the real despatch window — earliest despatch + fastest transit through
   * latest despatch + slowest transit. Business-day transit skips weekends;
   * calendar-day transit (carrier ranges, which can include Saturday delivery)
   * only rolls Sundays.
   *
   * @param {object} rate
   * @param {{quoteDate: Date, despatchMin: Date, despatchMax: Date, stateCode: string|null}} ctx
   * @returns {{transit: {min: number, max: number}|null, text: string}}
   */
  _rateEta(rate, ctx) {
    const transit = this._transitForRate(rate, ctx);
    if (!transit) return { transit: null, text: '' };

    const days =
      transit.min === transit.max
        ? `${transit.min} day${transit.min === 1 ? '' : 's'}`
        : `${transit.min}–${transit.max} days`;

    const add = transit.unit === 'business' ? addBusinessDays : (date, n) => skipSunday(addCalendarDays(date, n));
    const start = add(ctx.despatchMin, transit.min);
    const end = add(ctx.despatchMax, transit.max);

    // Dates failed a sanity gate — still surface the transit span so the row
    // stays sortable and shows "1–2 days" without an arrival window.
    if (end < start) return { transit, text: days };

    return { transit, text: `${days} · ${formatDayRange(start, end)}` };
  }

  // ── Rendering ────────────────────────────────────────────────────────────

  /** @param {boolean} busy */
  _setBusy(busy) {
    if (!this._button) return;
    this._button.disabled = busy;
    this._button.setAttribute('aria-busy', String(busy));
  }

  /**
   * Push a terse string to the dedicated live region. The visible results are
   * not themselves aria-live, so re-sorting announces only "Sorted by …" here
   * instead of re-reading every rate row.
   * @param {string} message
   */
  _announce(message) {
    if (!this._live || !message) return;
    this._live.textContent = message;
  }

  /**
   * @param {string} text
   * @param {'error'|'pending'|'info'} tone — only 'error' gets modifier CSS
   *   (red); everything else inherits the muted base. Guidance, not alarm.
   */
  _renderMessage(text, tone) {
    this._output.hidden = false;
    this._output.innerHTML = '';

    const p = document.createElement('p');
    p.className = tone === 'error' ? 'cse-message cse-message--error' : 'cse-message';
    p.textContent = text;
    this._output.appendChild(p);

    // The visible message isn't in a live region anymore — announce it here.
    this._announce(text);
  }

  _clearOutput() {
    this._output.innerHTML = '';
    this._output.hidden = true;
    this._lastFingerprint = null;
    // Explicit '' clears the price note; reserved --known width still holds.
    this._setTriggerSummary((this._input?.value || '').trim(), '');
  }

  /**
   * PDP pill copy:
   *   no postcode → "Check delivery"
   *   known, loading / cleared → "Delivery to 2250"
   *   free → "2250 · Free delivery" (CSS adds the ·)
   *   paid → "2250 · From $9.20"
   * Never "From Free". aria-label keeps full English.
   *
   * @param {string} [postcode]
   * @param {string} [cheapestLabel] 'Free' or formatted money; '' clears the
   *   note and shows the loading label; omit (undefined) to leave a quoted
   *   note alone (stale-while-revalidate)
   */
  /** "Delivery to <strong>2000</strong>" — the postcode is the bit that changes. */
  _setDeliveryLabel(postcode) {
    const strong = document.createElement('strong');
    strong.textContent = postcode;
    this._triggerLabel.replaceChildren('Delivery to ', strong);
  }

  _setTriggerSummary(postcode = '', cheapestLabel = undefined) {
    if (!this._triggerLabel && !this._triggerNote) return;

    const known = /^\d{4}$/.test(postcode);
    const quoted = this._trigger?.classList.contains('cse-trigger--quoted');

    this._trigger?.classList.toggle('cse-trigger--known', known);
    if (!known) this._trigger?.classList.remove('cse-trigger--quoted');

    if (this._triggerLabel) {
      if (!known) {
        this._triggerLabel.textContent = 'Check delivery';
      } else if (cheapestLabel) {
        // Quote landed — short postcode; price lives in the note.
        this._triggerLabel.textContent = postcode;
      } else if (cheapestLabel === '') {
        // Cleared / waiting on a fresh quote.
        this._setDeliveryLabel(postcode);
      } else if (!quoted) {
        // First paint with a known postcode (auto-quote still pending).
        this._setDeliveryLabel(postcode);
      }
      // else: stale-while-revalidate — keep the short postcode label
    }

    // Accessible name stays full English. Omit cheapestLabel (undefined) keeps
    // a priced aria while the visible note is stale-while-revalidate; upgrade
    // the Liquid invite label once a postcode is known.
    if (this._trigger && cheapestLabel !== undefined) {
      let aria = 'Check delivery';
      if (known && cheapestLabel) {
        aria =
          cheapestLabel === 'Free'
            ? `Delivery to ${postcode}, Free delivery`
            : `Delivery to ${postcode}, from ${cheapestLabel}`;
      } else if (known) {
        aria = `Delivery to ${postcode}`;
      }
      this._trigger.setAttribute('aria-label', aria);
    } else if (this._trigger && !known) {
      this._trigger.setAttribute('aria-label', 'Check delivery');
    } else if (this._trigger && known) {
      const current = this._trigger.getAttribute('aria-label') || '';
      if (!current || current === 'Check delivery' || current === 'Check delivery options') {
        this._trigger.setAttribute('aria-label', `Delivery to ${postcode}`);
      }
    }

    if (!this._triggerNote || cheapestLabel === undefined) return;

    if (known && cheapestLabel) {
      this._triggerNote.textContent =
        cheapestLabel === 'Free' ? 'Free delivery' : `From ${cheapestLabel}`;
      this._trigger?.classList.add('cse-trigger--quoted');
    } else {
      this._triggerNote.textContent = '';
      this._trigger?.classList.remove('cse-trigger--quoted');
    }
  }

  /**
   * Loading state. The product dialog gets skeleton rows shaped like the
   * result (shimmer in CSS, static under prefers-reduced-motion) so the
   * reveal doesn't jump; the cart panel keeps its text line. Screen readers
   * get words either way.
   */
  _renderPending() {
    if (this._mode !== 'product') {
      this._renderMessage('Checking live carrier rates…', 'pending');
      return;
    }

    this._output.hidden = false;
    this._output.innerHTML = '';

    const skeleton = document.createElement('ul');
    skeleton.className = 'cse-skel';
    skeleton.setAttribute('aria-hidden', 'true');

    for (let i = 0; i < 3; i++) {
      const row = document.createElement('li');
      row.className = 'cse-skel__row';

      const name = document.createElement('span');
      name.className = 'cse-skel__bar cse-skel__bar--name';

      const price = document.createElement('span');
      price.className = 'cse-skel__bar cse-skel__bar--price';

      row.append(name, price);
      skeleton.appendChild(row);
    }

    this._output.append(skeleton);
    this._announce('Checking live carrier rates…');
  }

  /**
   * One rate row: carrier name + real freight price, a "1–2 days · Mon 27 Jul"
   * estimate under it, and (on the sort's top pick) a chip naming why it leads.
   * Every rate is shown in full — no expand/collapse; the sort control orders
   * them, checkout still chooses the actual method.
   *
   * @param {{title: string, grossCents: number, etaText: string, fast: boolean, isLocal: boolean}} model
   * @param {{leadLabel: string|null, showFast: boolean}} opts leadLabel is the
   *   sort chip ('Cheapest' / 'Fastest' / 'Fastest value') for the #1 row, else
   *   null; showFast draws the "Fast" tag on quickest-tier rows
   * @returns {HTMLElement}
   */
  _buildRateRow(model, opts) {
    const item = document.createElement('li');
    item.className = 'cse-rate';
    if (opts.leadLabel) item.classList.add('cse-rate--best');
    if (model.fast) item.classList.add('cse-rate--fast');
    if (model.isLocal) item.classList.add('cse-rate--local');

    const name = document.createElement('span');
    name.className = 'cse-rate__name';
    name.textContent = model.title;

    const price = document.createElement('span');
    price.className = 'cse-rate__price';
    if (model.grossCents === 0) {
      price.textContent = 'Free';
      price.classList.add('cse-rate__price--free');
    } else {
      price.textContent = formatMoney(model.grossCents);
    }

    item.append(name, price);

    if (opts.leadLabel || opts.showFast || model.etaText) {
      const meta = document.createElement('span');
      meta.className = 'cse-rate__meta';

      if (opts.leadLabel) {
        const pill = document.createElement('span');
        pill.className = 'cse-rate__pill';
        pill.textContent = opts.leadLabel;
        meta.appendChild(pill);
      }

      if (opts.showFast) {
        const fast = document.createElement('span');
        fast.className = 'cse-rate__fast';
        fast.appendChild(buildBoltIcon());
        const fastLabel = document.createElement('span');
        fastLabel.textContent = 'Fast';
        fast.appendChild(fastLabel);
        meta.appendChild(fast);
      }

      if (model.etaText) {
        const eta = document.createElement('span');
        eta.className = 'cse-rate__eta';
        eta.textContent = model.etaText;
        meta.appendChild(eta);
      }

      item.appendChild(meta);
    }

    if (SHOW_LOCAL_DELIVERY_POLICY && model.isLocal) {
      item.appendChild(buildLocalDeliveryDetail());
    }

    return item;
  }

  /**
   * Segmented Cheapest / Fastest / Best value control. Re-orders the rates the
   * shopper already has — no carrier round-trip. aria-pressed buttons in a
   * labelled group so the active mode is announced.
   *
   * @param {string} activeMode
   * @returns {HTMLElement}
   */
  _buildSortControl(activeMode) {
    const group = document.createElement('div');
    group.className = 'cse-sort';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-label', 'Sort delivery options');

    for (const mode of RATE_SORT_ORDER) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cse-sort__btn';
      btn.textContent = RATE_SORTS[mode].label;
      btn.setAttribute('aria-pressed', mode === activeMode ? 'true' : 'false');
      btn.addEventListener('click', () => {
        if (this._sortMode === mode) return;
        this._sortMode = mode;
        this._paintRates();
        // Keep focus on the control after the re-render swaps the button out.
        this._output.querySelector('.cse-sort__btn[aria-pressed="true"]')?.focus();
        // Terse announcement instead of re-reading the whole re-sorted list.
        this._announce(`Sorted by ${RATE_SORTS[mode].label.toLowerCase()}`);
      });
      group.appendChild(btn);
    }

    return group;
  }

  /**
   * Quote → rate models. Builds the sortable model list (real gross price +
   * transit span + ETA text) once, stashes it, and paints. Re-sorting reuses
   * the stash without re-quoting.
   *
   * @param {object[]} rates
   * @param {string} postcode
   * @param {number} quantity
   * @param {Date} quotedAt when the carrier answered — cached replays pass
   *   the original moment so delivery_range converts against the right day
   * @param {number} basisCents order value for the discount tier
   */
  _renderRates(rates, postcode, quantity, quotedAt, basisCents) {
    this._output.hidden = false;

    if (!rates.length) {
      // An empty answer means "no per-item quote came back", NOT "we don't
      // ship there" — never say the latter.
      this._output.innerHTML = '';
      this._renderMessage(
        this._mode === 'product'
          ? `Per-item rates aren't available for ${postcode} — add to cart for a full shipping estimate, and exact rates at checkout.`
          : `No live rate to ${postcode} for what's in your cart — contact us and we'll quote it manually.`,
        this._mode === 'product' ? 'info' : 'error'
      );
      return;
    }

    // ETA context, resolved once per render: despatch window is live state
    // (cutoff rolls over at 1pm, ready window may have landed since).
    const despatch = this._despatchWindow();
    const ctx = {
      quoteDate: quotedAt,
      despatchMin: despatch.min,
      despatchMax: despatch.max,
      stateCode: stateForPostcode(postcode)?.code || null,
    };

    // Real carrier price per row (the order discount is explained once, below).
    const models = rates.map((rate) => {
      const title = rate.presentment_name || rate.name || 'Shipping';
      const eta = this._rateEta(rate, ctx);
      return {
        title,
        grossCents: Math.round(Number(rate.price) * 100) || 0,
        transit: eta.transit,
        etaText: eta.text,
        fast: !!eta.transit && eta.transit.max <= FAST_TRANSIT_MAX_DAYS,
        isLocal: /local delivery/i.test(title),
      };
    });

    this._rateRender = { models, discount: shippingDiscountFor(basisCents), postcode };
    this._paintRates();
    this._announce(`${models.length} delivery ${models.length === 1 ? 'option' : 'options'} to ${postcode}`);

    // Product dialog extras: the trigger chip remembers the answer
    // ("2250 · Free delivery" / "2250 · From $9.20") so the PDP shows it at a
    // glance, and the despatch footer refreshes alongside the rates. The pill
    // shows the shopper's EFFECTIVE cheapest — cheapest carrier price net of the
    // order discount (the list itself shows real prices + one discount note).
    if (this._mode === 'product') {
      const discount = this._rateRender.discount;
      const cheapestGross = models.reduce((lo, m) => (m.grossCents < lo ? m.grossCents : lo), Infinity);
      const cheapestNet = discount ? Math.max(0, cheapestGross - discount.cents) : cheapestGross;
      this._setTriggerSummary(postcode, cheapestNet === 0 ? 'Free' : formatMoney(cheapestNet));
      this._lastFingerprint = this._shippingFingerprint(this._variantId, quantity);
      this._fillDespatch();
      this._fillQuoteQty();
    } else {
      this._fillDespatch();
    }
  }

  /**
   * Paint (or re-paint) the stashed rate models under the active sort — the
   * heading + sort control, the rate list, then the order-discount / local /
   * regional notes. Called on a fresh quote and on every sort change.
   */
  _paintRates() {
    if (!this._rateRender) return;

    const { models, discount, postcode } = this._rateRender;
    const sortMode = RATE_SORTS[this._sortMode] ? this._sortMode : 'cheapest';
    const multi = models.length > 1;

    this._output.innerHTML = '';

    const headingId = `cse-rates-head-${this._rateGroupName || 'x'}`;
    const head = document.createElement('div');
    head.className = 'cse-result-head';

    const title = document.createElement('p');
    title.className = 'cse-result-head__title';
    title.id = headingId;
    title.textContent =
      this._mode === 'product'
        ? `Delivery options to ${postcode}`
        : `Shipping your cart to ${postcode}:`;
    head.appendChild(title);

    // Sorting one option is meaningless — only offer it with a real choice.
    if (multi) head.appendChild(this._buildSortControl(sortMode));
    this._output.appendChild(head);

    const list = document.createElement('ul');
    list.className = 'cse-rates';
    list.setAttribute('role', 'list');
    list.setAttribute('aria-labelledby', headingId);

    const sorted = [...models].sort(RATE_SORTS[sortMode].compare);

    let anyEta = false;
    let allLocal = true;

    // Under a speed sort the lead chip already names speed, so the fast tag is
    // suppressed on that row to avoid "Fastest · Fast" doubling up.
    const speedSort = sortMode === 'fastest' || sortMode === 'value';

    sorted.forEach((model, index) => {
      if (!/local delivery|pick ?up/i.test(model.title)) allLocal = false;
      if (model.etaText) anyEta = true;
      const isLead = index === 0 && multi;
      const leadLabel = isLead ? RATE_SORTS[sortMode].label : null;
      const showFast = model.fast && !(isLead && speedSort);
      list.appendChild(this._buildRateRow(model, { leadLabel, showFast }));
    });

    this._output.appendChild(list);

    // One calm callout for the order-total discount — real prices above sit
    // higher than the checkout total without it, so this explains the gap.
    if (discount) {
      const cheapest = models.reduce((lo, m) => (m.grossCents < lo ? m.grossCents : lo), Infinity);
      const note = document.createElement('p');
      note.className = 'cse-note cse-note--discount';
      note.textContent =
        cheapest > 0 && cheapest <= discount.cents
          ? `You'll also get −${formatMoney(discount.cents)} off your order total at checkout (orders ${discount.label}) — that covers the cheapest option here, so it effectively ships free.`
          : `You'll also get −${formatMoney(discount.cents)} off your order total at checkout (orders ${discount.label}).`;
      this._output.appendChild(note);
    }

    // Local Delivery postcodes get only the native local option from the
    // cart API — reassure locals the couriers still exist. Phaser: off.
    if (SHOW_LOCAL_DELIVERY_POLICY && this._mode === 'product' && allLocal) {
      const localNote = document.createElement('p');
      localNote.className = 'cse-note';
      localNote.textContent = "You're in our Local Delivery zone — courier options also show at checkout.";
      this._output.appendChild(localNote);
    }

    // Rhys: "make sure you note regional areas take longer".
    if (anyEta) {
      const regional = document.createElement('p');
      regional.className = 'cse-note';
      regional.textContent = REGIONAL_NOTE;
      this._output.appendChild(regional);
    }
  }
}

if (!customElements.get('ccc-shipping-estimate')) {
  customElements.define('ccc-shipping-estimate', CccShippingEstimate);
}
